import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { INSIGHTS, formatDate } from "@/lib/insights";

export default function InsightsPreview() {
    const items = INSIGHTS.slice(0, 3);
    return (
        <section id="resources" className="relative py-24 lg:py-36 border-t border-navy/10" data-testid="insights-preview-section">
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">Resources &amp; Research</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
                            Whitepapers, field notes &amp; events <em className="text-gold not-italic font-light">from the risk desk.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end flex flex-col justify-between gap-6">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Argument-led writing on the frameworks, cash flows, and counterparties that will define climate as financial risk in the next decade.
                        </p>
                        <Link to="/resources" className="btn-ghost self-start" data-testid="insights-see-all">
                            All Resources <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Featured */}
                    <FeaturedCard item={items[0]} className="lg:col-span-8 lg:row-span-2" featured />
                    <FeaturedCard item={items[1]} className="lg:col-span-4" />
                    <FeaturedCard item={items[2]} className="lg:col-span-4" />
                </div>
            </div>
        </section>
    );
}

function FeaturedCard({ item, className = "", featured = false }) {
    if (!item) return null;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className={className}
        >
            <Link
                to={`/resources/${item.slug}`}
                className="group block h-full relative overflow-hidden border border-navy/10 hover:border-gold/60 transition-all duration-500 bg-white"
                data-testid={`insight-card-${item.slug}`}
            >
                <div className={`relative overflow-hidden ${featured ? "aspect-[16/9]" : "aspect-[16/10]"}`}>
                    <motion.img
                        src={item.hero}
                        alt=""
                        className="w-full h-full object-cover"
                        style={{ filter: "grayscale(50%) contrast(1.05) brightness(0.75)" }}
                        whileHover={{ scale: 1.04 }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-paper via-paper/20 to-transparent"></div>
                    <div className="absolute top-5 left-5 flex items-center gap-2">
                        <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-gold bg-paper/70 backdrop-blur-sm px-3 py-1.5 border border-gold/30">
                            {item.type}
                        </span>
                        <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-navy/70 bg-paper/70 backdrop-blur-sm px-3 py-1.5 border border-navy/15">
                            {item.category}
                        </span>
                    </div>
                    <div className="absolute top-5 right-5 font-mono text-[9px] tracking-[0.22em] uppercase text-navy/60">
                        {item.chapter}
                    </div>
                </div>

                <div className={`p-6 lg:p-8 ${featured ? "lg:p-10" : ""}`}>
                    <div className="flex items-center gap-4 font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40 mb-4">
                        <span>{item.readMinutes} min read</span>
                    </div>
                    <h3 className={`font-serif leading-[1.05] tracking-[-0.02em] text-navy group-hover:text-gold transition-colors duration-500 ${featured ? "text-3xl md:text-4xl lg:text-5xl" : "text-xl md:text-2xl"}`}>
                        {item.title}
                    </h3>
                    {featured && (
                        <p className="mt-5 text-navy/60 text-base leading-relaxed max-w-[560px]">
                            {item.subtitle}
                        </p>
                    )}
                    <div className={`mt-6 flex items-center gap-2 text-gold font-mono text-[11px] tracking-[0.22em] uppercase ${featured ? "" : "text-[10px]"}`}>
                        Read <ArrowUpRight size={14} className="group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-500" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
